import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import OrderViewDialog from "@/components/OrderViewDialog";
import CustomerLink from "@/components/CustomerLink";

/**
 * Today's Orders — dashboard widget.
 *
 * Extracted from the legacy monolithic Dashboard.jsx so the page can render
 * widgets dynamically from the permission-driven registry. Self-contained:
 * owns its data fetch, refresh, and the OrderViewDialog modal for row clicks.
 *
 * DSG-001 compliant — uses `StatusBadge` sub-component with semantic tokens.
 *
 * PO directive 2026-02-04 · Remove Duplicate Date/Time — the date+time
 * previously shown under "Today's Orders" duplicated the Greeting Card and
 * has been removed. The header now shows an operational summary line only
 * ("N Orders Today" / "No orders have been created today.").
 */
export default function TodaysOrdersWidget() {
  const [orders, setOrders] = useState([]);
  const [activeCustomers, setActiveCustomers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  // ERP Sync freshness (PO 2026-02-04 adjustment) — the widget
  // previously showed "Last Updated" sourced from `Date.now()` at
  // dashboard refresh, which was misleading. It now shows the timestamp
  // of the most recent SUCCESSFUL ERP synchronization (Customers +
  // Delivery Details) via the System Status sync-sources API. Additional
  // sync sources (Pricing, Inventory, Drivers) register on the backend
  // without any widget change.
  const [erpSync, setErpSync] = useState(null);  // { last_success_at, last_success_by, label, description }

  const load = async () => {
    setLoading(true);
    try {
      const [o, c, s] = await Promise.all([
        api.get("/orders"),
        api.get("/customers/count"),
        api.get("/system/status/sync-sources"),
      ]);
      setOrders(o.data);
      setActiveCustomers(c.data.active);
      const erp = (s.data?.sources || []).find((x) => x.code === "erp_customers_delivery");
      setErpSync(erp || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayOrders = orders.filter((o) => new Date(o.created_at) >= today);
  const newToday = todayOrders.length;
  const sentToday = todayOrders.filter((o) => o.status === "Sent").length;
  const cancelledToday = todayOrders.filter((o) => o.status === "Cancelled").length;

  const stats = [
    { label: "New Orders Today", value: newToday, testid: "stat-new-today" },
    { label: "Sent Today", value: sentToday, testid: "stat-sent-today" },
    { label: "Cancelled Today", value: cancelledToday, testid: "stat-cancelled-today", emphasis: cancelledToday > 0 },
    { label: "Active Customers", value: activeCustomers, testid: "stat-active-customers" },
  ];

  const countLabel = todayOrders.length === 0
    ? "No orders have been created today."
    : `${todayOrders.length} ${todayOrders.length === 1 ? "Order" : "Orders"}`;

  // Freshness tone anchored to ERP Sync recency (PO 2026-02-04).
  // ERP imports are performed manually (weekly cadence typically), so
  // the thresholds are on a *days* scale — not the *minutes* scale of
  // page-load freshness. Rough defaults (tunable by PO):
  //   🟢 Current            — <7 days since last successful ERP sync
  //   🟡 Refresh Recommended — 7–14 days
  //   🔴 Out of Date         — >14 days
  //   ⚪ Unknown             — no successful sync recorded yet
  const erpAtDate = erpSync?.last_success_at ? new Date(erpSync.last_success_at) : null;
  const erpAgeDays = erpAtDate ? (Date.now() - erpAtDate.getTime()) / (1000 * 60 * 60 * 24) : null;
  const freshnessTone =
    erpAgeDays === null   ? "unknown" :
    erpAgeDays < 7        ? "current" :
    erpAgeDays < 14       ? "recommended" :
                            "stale";

  return (
    <div data-testid="todays-orders-widget">
      <div
        data-testid="dashboard-summary-bar"
        className="flex flex-wrap items-stretch divide-y divide-gray-200 border border-gray-200 bg-white sm:divide-x sm:divide-y-0"
      >
        {stats.map((s) => (
          <div
            key={s.label}
            data-testid={s.testid}
            className="flex flex-1 items-center justify-between gap-3 px-4 py-1.5 sm:py-2"
          >
            <span className="text-xs font-medium text-gray-600">{s.label}</span>
            <span
              className={`text-lg font-semibold leading-none ${
                s.emphasis && s.value > 0 ? "text-[#EB1D25]" : "text-gray-900"
              }`}
            >
              {loading ? "—" : s.value}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 border border-gray-200 bg-white">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-gray-900">Today&apos;s Orders</h2>
            <p data-testid="dashboard-period" className="mt-1 text-sm text-gray-500">
              Period: <span className="font-medium text-gray-700">Today</span>
            </p>
            <p data-testid="dashboard-order-count" className="mt-0.5 text-sm text-gray-500">
              {countLabel}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <DataFreshness
              lastRefreshedAt={erpAtDate}
              tone={freshnessTone}
              label={erpSync?.label || "ERP Sync"}
              description={erpSync?.description}
            />
            <button
              data-testid="dashboard-refresh"
              onClick={load}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowsClockwise size={14} weight="bold" /> Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table data-testid="todays-orders-table" className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-[#f3f4f6] text-left text-sm text-gray-600">
                <th className="px-5 py-3 font-semibold">Time</th>
                <th className="px-5 py-3 font-semibold">Order #</th>
                <th className="px-5 py-3 font-semibold">Customer</th>
                <th className="px-5 py-3 font-semibold">Products</th>
                <th className="px-5 py-3 font-semibold">Assigned Driver</th>
                <th className="px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {todayOrders.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-[15px] text-gray-500">
                    No orders have been created today.
                  </td>
                </tr>
              )}
              {todayOrders.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`order-row-${o.order_number}`}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                >
                  <td className="px-5 py-4 font-mono text-[15px] text-gray-700">
                    {new Date(o.created_at).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false })}
                  </td>
                  <td className="px-5 py-4 font-mono text-[15px]">
                    <button
                      data-testid={`view-order-${o.order_number}`}
                      onClick={async () => {
                        setViewOpen(true);
                        setViewOrder(null);
                        try {
                          const res = await api.get(`/orders/${o.id}`);
                          setViewOrder(res.data);
                        } catch (e) {
                          setViewOpen(false);
                          toast.error(e?.response?.data?.detail || "Could not load order");
                        }
                      }}
                      className="rounded-sm text-gray-700 underline-offset-2 hover:text-[#EB1D25] hover:underline focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
                    >
                      {o.order_number}
                    </button>
                  </td>
                  <td className="px-5 py-4 text-[15px] font-medium text-gray-900">
                    <CustomerLink customerId={o.customer_id} customerName={o.customer_name} testId={`todays-order-customer-${o.order_number}`} />
                  </td>
                  <td className="px-5 py-4 text-[15px] text-gray-700">
                    {o.products.map((p) => p.product_name).join(", ")}
                  </td>
                  <td className="px-5 py-4 text-[15px] text-gray-700">
                    {o.driver_names && o.driver_names.length > 0 ? o.driver_names.join(", ") : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={o.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <OrderViewDialog
        open={viewOpen}
        order={viewOrder}
        onClose={() => { setViewOpen(false); setViewOrder(null); }}
        onChanged={(updated) => {
          setViewOrder(updated);
          setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
        }}
      />
    </div>
  );
}

/**
 * DataFreshness — ERP Sync indicator (PO 2026-02-04 adjustment).
 *
 * Previously showed the timestamp of the last dashboard refresh — which
 * was misleading because the number changed on every page load and did
 * not represent authoritative data currency. The widget now shows the
 * timestamp of the most recent SUCCESSFUL ERP synchronization (Customers
 * + Delivery Details) coming from the new System Status sync-sources
 * framework (`GET /api/system/status/sync-sources`).
 *
 * Future architecture — additional sync sources (Pricing, Inventory,
 * Drivers, etc.) register on the backend; the same widget can be
 * cloned or a compact status strip built for the header without
 * changing this component's contract.
 *
 * Props:
 *   lastRefreshedAt : Date | null   — sync source's last_success_at parsed
 *   tone            : "current" | "recommended" | "stale" | "unknown"
 *   label           : string        — display name ("ERP Sync")
 *   description     : string        — hover tooltip text
 *
 * Status dot colours (unchanged):
 *   🟢 tone === "current"     — <7 days since last successful ERP sync
 *   🟡 tone === "recommended" — 7–14 days
 *   🔴 tone === "stale"       — >14 days
 *   ⚪ tone === "unknown"     — no successful sync recorded yet
 */
function DataFreshness({ lastRefreshedAt, tone, label = "ERP Sync", description }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const dot = { current: "bg-emerald-500", recommended: "bg-amber-500", stale: "bg-red-500", unknown: "bg-gray-300" }[tone] || "bg-gray-300";
  const tooltip = description || "Last successful synchronization of Customers and Delivery Details from the ERP.";

  // Render even when no sync has ever happened — the "Never synced"
  // state is itself operationally important (⚪ Unknown) and must not
  // be silent.
  if (!lastRefreshedAt) {
    return (
      <div
        data-testid="dashboard-erp-sync"
        title={tooltip}
        className="hidden text-right text-xs text-gray-500 sm:block"
      >
        <div className="flex items-center justify-end gap-1.5 font-semibold text-gray-600">
          <span data-testid="dashboard-erp-sync-dot" className={`inline-block h-2 w-2 rounded-full ${dot}`} />
          {label}
        </div>
        <div data-testid="dashboard-erp-sync-status" className="mt-0.5 italic leading-tight text-gray-400">Never synced</div>
      </div>
    );
  }

  const dateStr = lastRefreshedAt.toLocaleDateString("en-CA", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
  const timeStr = lastRefreshedAt
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .replace(/\s?AM\b/, " a.m.")
    .replace(/\s?PM\b/, " p.m.");

  return (
    <div
      data-testid="dashboard-erp-sync"
      title={tooltip}
      className="hidden text-right text-xs text-gray-500 sm:block"
    >
      <div className="flex items-center justify-end gap-1.5 font-semibold text-gray-600">
        <span data-testid="dashboard-erp-sync-dot" className={`inline-block h-2 w-2 rounded-full ${dot}`} />
        {label}
      </div>
      <div data-testid="dashboard-erp-sync-date" className="mt-0.5 leading-tight">{dateStr}</div>
      <div data-testid="dashboard-erp-sync-time" className="font-mono leading-tight tabular-nums">{timeStr}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Draft:     "bg-surface-alt text-ink-secondary border-divider-strong",
    Sent:      "bg-status-info-bg text-status-info-fg border-status-info-bg",
    Cancelled: "bg-status-error-bg text-status-error-fg border-status-error-bg",
    Delivered: "bg-status-success-bg text-status-success-fg border-status-success-bg",
  };
  return (
    <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-sm font-medium ${styles[status] || styles.Sent}`}>
      {status}
    </span>
  );
}
